import React, { useState, useEffect } from 'react';
import { graphBuilderApi } from '../../services/api';
import './Explainability.css';

/**
 * Evidence Evaluation Timeline
 *
 * Shows business-language milestones of how the evaluation progressed.
 * Each step is expandable; no platform internals are exposed.
 */
const EvidenceTimeline = ({
  timeline: timelineProp = [],
  evidenceFactors = [],
  excludedItems = [],
  decisionRules = [],
  executionId = '',
  insightId = '',
  domain = 'general',
  onStepClick,
}) => {
  const [expandedStep, setExpandedStep] = useState(null);
  const [fetchedTimeline, setFetchedTimeline] = useState(null);

  const timeline = (timelineProp && timelineProp.length > 0) ? timelineProp : (fetchedTimeline || []);

  useEffect(() => {
    if (timelineProp && timelineProp.length > 0) return;
    if (!executionId || fetchedTimeline) return;
    graphBuilderApi.getEvaluationTimeline(executionId, domain, null, insightId)
      .then(data => {
        if (data?.timeline) setFetchedTimeline(data.timeline);
        else if (data?.evaluation_timeline) setFetchedTimeline(data.evaluation_timeline);
      })
      .catch(() => {});
  }, [timelineProp, executionId, insightId, domain, fetchedTimeline]);

  const handleStepClick = (index, step) => {
    setExpandedStep(expandedStep === index ? null : index);
    if (onStepClick) onStepClick(step);
  };

  const getExpandableContent = (step) => {
    const key = step.expandable_key;
    if (!key) return null;

    if (key === 'evidence_factors' && evidenceFactors.length > 0) {
      return (
        <div className="explain-timeline-detail">
          <h4>Evidence Factors Assessed</h4>
          <table className="explain-detail-table">
            <thead>
              <tr>
                <th>Factor</th>
                <th>Category</th>
                <th>Source</th>
                <th>Strength</th>
              </tr>
            </thead>
            <tbody>
              {evidenceFactors.map((f, i) => (
                <tr key={i}>
                  <td>{f.name}</td>
                  <td>{f.category}</td>
                  <td>{f.source_document}</td>
                  <td>
                    <span className={`explain-strength explain-strength--${(f.evidence_strength || '').toLowerCase()}`}>
                      {f.evidence_strength}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (key === 'excluded' && excludedItems.length > 0) {
      return (
        <div className="explain-timeline-detail">
          <h4>Excluded Information</h4>
          <ul className="explain-excluded-list">
            {excludedItems.map((item, i) => (
              <li key={i}>
                <strong>{item.name}</strong>: {item.reason}
                {item.source_document && (
                  <span className="explain-source-tag"> ({item.source_document})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (key === 'decision_rules' && decisionRules.length > 0) {
      return (
        <div className="explain-timeline-detail">
          <h4>Decision Rules Evaluated</h4>
          {decisionRules.map((rule, i) => (
            <div key={i} className="explain-rule-card">
              <div className="explain-rule-header">
                <span className={`explain-rule-status explain-rule-status--${rule.satisfied ? 'satisfied' : 'not-satisfied'}`}>
                  {rule.satisfied ? 'SATISFIED' : 'NOT SATISFIED'}
                </span>
                <strong>{rule.rule_name}</strong>
              </div>
              {rule.rule_description && <p className="explain-rule-desc">{rule.rule_description}</p>}
              {rule.source_policy && <p className="explain-rule-source">Source: {rule.source_policy}</p>}
              {rule.criteria && rule.criteria.length > 0 && (
                <ul className="explain-criteria-list">
                  {rule.criteria.map((c, j) => (
                    <li key={j} className={c.satisfied ? 'explain-criterion--met' : 'explain-criterion--not-met'}>
                      {c.satisfied ? '\u2713' : '\u2717'} {c.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  if (!timeline || timeline.length === 0) {
    return <div className="explain-empty">No evaluation timeline available.</div>;
  }

  return (
    <div className="explain-timeline">
      <h3 className="explain-section-title">Evaluation Timeline</h3>
      <div className="explain-timeline-track">
        {timeline.map((step, index) => {
          const isExpanded = expandedStep === index;
          const hasDetail = step.expandable_key || step.detail;
          const isLast = index === timeline.length - 1;

          return (
            <div key={index} className="explain-timeline-step">
              <div className="explain-timeline-connector">
                <div className={`explain-timeline-dot ${isLast ? 'explain-timeline-dot--final' : ''}`} />
                {!isLast && <div className="explain-timeline-line" />}
              </div>
              <div
                className={`explain-timeline-content ${hasDetail ? 'explain-timeline-content--clickable' : ''}`}
                onClick={() => hasDetail && handleStepClick(index, step)}
              >
                <div className="explain-timeline-label">
                  {step.timestamp && (
                    <span className="explain-timeline-time">{step.timestamp}</span>
                  )}
                  <span className="explain-timeline-text">{step.label}</span>
                  {hasDetail && (
                    <span className="explain-timeline-expand">
                      {isExpanded ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </div>
                {step.detail && !step.expandable_key && isExpanded && (
                  <p className="explain-timeline-detail-text">{step.detail}</p>
                )}
                {isExpanded && getExpandableContent(step)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EvidenceTimeline;
